import { type FC, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useOrgApiKeys } from "../../context/OrgApiKeysContext";
import { useAllRepositories } from "../../hooks/useAllRepositories";
import { useRepositoryIndexingFailures } from "../../hooks/useRepositoryIndexingFailures";
import { useRepositoryIndexingInsights } from "../../hooks/useRepositoryIndexingInsights";
import { useRepositoryIndexingRuns } from "../../hooks/useRepositoryIndexingRuns";
import { useRepositoryTopology } from "../../hooks/useRepositoryTopology";
import { PanelHeaderBlock } from "../common/PanelHeaderBlock";
import { IndexingRepositoryList } from "./IndexingRepositoryList";
import { IndexingRunConfigModal } from "./IndexingRunConfigModal";
import { IndexingSummaryCards } from "./IndexingSummaryCards";
import { IndexingToolbar } from "./IndexingToolbar";
import { RepositoryIndexingFailuresPanel } from "./RepositoryIndexingFailuresPanel";
import { RepositoryIndexingInsightsPanel } from "./RepositoryIndexingInsightsPanel";
import { RepositoryIndexingRunsPanel } from "./RepositoryIndexingRunsPanel";
import { RepositoryTopologyGraph } from "./RepositoryTopologyGraph";

export const IndexingPanel: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedRepositorySlug, setSelectedRepositorySlug] = useState<
    string | null
  >(null);
  const [isRunConfigOpen, setRunConfigOpen] = useState(false);
  const [preselectedSlug, setPreselectedSlug] = useState<string | null>(null);

  useEffect(() => {
    const action = searchParams.get("action");
    const repo = searchParams.get("repo");
    if (action === "index") {
      setPreselectedSlug(repo);
      setRunConfigOpen(true);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("action");
          next.delete("repo");
          return next;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams]);

  const { missingProviders } = useOrgApiKeys();
  const { repositories, loading: reposLoading } = useAllRepositories({
    freshness: true,
  });

  const {
    runs,
    loading: runsLoading,
    error: runsError,
    triggering: triggeringRun,
    selectedRunId,
    selectedRun,
    refreshRuns,
    triggerManualRun,
    selectRun,
  } = useRepositoryIndexingRuns();

  const {
    insights,
    loading: insightsLoading,
    error: insightsError,
  } = useRepositoryIndexingInsights(selectedRunId, selectedRepositorySlug);

  const {
    failures,
    loading: failuresLoading,
    error: failuresError,
  } = useRepositoryIndexingFailures(selectedRunId, selectedRepositorySlug);

  const {
    nodes: topologyNodes,
    relationships: topologyRelationships,
    loading: topologyLoading,
    error: topologyError,
  } = useRepositoryTopology();

  const latestRun = runs.length > 0 ? runs[0] : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <PanelHeaderBlock
          eyebrow="Repository Indexing"
          title="Indexing Workspace"
          description="Pick a repository scope, select a run, and inspect insights in one place."
        />
        <IndexingToolbar
          onRefresh={() => void refreshRuns()}
          onTriggerRun={() => setRunConfigOpen(true)}
          loading={runsLoading}
          triggering={triggeringRun}
        />
      </div>

      <IndexingSummaryCards
        repositories={repositories}
        runs={runs}
        insights={insights}
        loading={reposLoading || runsLoading || insightsLoading}
        scope={selectedRepositorySlug}
      />

      {(topologyRelationships.length > 0 || topologyLoading) && (
        <RepositoryTopologyGraph
          nodes={topologyNodes}
          relationships={topologyRelationships}
          loading={topologyLoading}
          error={topologyError}
        />
      )}

      <div className="grid gap-4 xl:grid-cols-[320px,1fr]">
        <div className="space-y-4">
          <IndexingRepositoryList
            loading={reposLoading}
            repositories={repositories}
            selectedRepositorySlug={selectedRepositorySlug}
            onSelect={setSelectedRepositorySlug}
          />
          <RepositoryIndexingRunsPanel
            runs={runs}
            loading={runsLoading}
            error={runsError}
            selectedRunId={selectedRunId}
            onRefresh={() => void refreshRuns()}
            onSelectRun={selectRun}
          />
        </div>

        <div className="space-y-4">
          <RepositoryIndexingFailuresPanel
            selectedRun={selectedRun}
            failures={failures}
            loading={failuresLoading}
            error={failuresError}
            repositorySlug={selectedRepositorySlug}
          />
          <RepositoryIndexingInsightsPanel
            selectedRun={selectedRun}
            insights={insights}
            loading={insightsLoading}
            error={insightsError}
            eyebrow={
              selectedRepositorySlug
                ? "Scoped to Repository"
                : "All Repositories"
            }
            title={
              selectedRepositorySlug
                ? (selectedRepositorySlug.split("/").pop() ?? "Insights")
                : "Run Insights"
            }
            description={
              selectedRepositorySlug
                ? `Filtered to ${selectedRepositorySlug}`
                : "Insights captured during the selected indexing run."
            }
          />
        </div>
      </div>

      <IndexingRunConfigModal
        isOpen={isRunConfigOpen}
        repositories={repositories}
        latestRunId={latestRun?.run_id ?? null}
        missingProviders={missingProviders}
        preselectedSlug={preselectedSlug}
        submitting={triggeringRun}
        submitError={runsError}
        onClose={() => {
          setRunConfigOpen(false);
          setPreselectedSlug(null);
        }}
        onSubmit={async (repositoryModes) => {
          const triggered = await triggerManualRun(repositoryModes);
          if (triggered) {
            setRunConfigOpen(false);
            setPreselectedSlug(null);
          }
        }}
      />
    </div>
  );
};
